/**
 * What the application knows about who is signed in.
 *
 * The context lives apart from the provider so that editing the provider does
 * not reset React Fast Refresh state — the same split as the theme, access and
 * toast packages.
 */

import { createContext, useContext } from "react";
import type { User } from "@hardauto/core-api";

/** Where the session is in its lifecycle. */
export type SessionStatus =
  /** Restoring from the refresh cookie. Nothing may be decided yet. */
  | "restoring"
  | "anonymous"
  | "authenticated"
  /**
   * Credentials accepted, second factor outstanding.
   *
   * A distinct state rather than "still anonymous": the challenge id is a
   * credential, the user has a pending obligation, and the interface must show
   * the code prompt rather than the sign-in form.
   */
  | "mfa_required";

/** A second factor the server is waiting on. */
export interface MfaChallenge {
  challengeId: string;
  /** `totp`, `email_otp`, … — decides the wording and the input mode. */
  method: string;
  /** Where the code went, already masked by the server. */
  sentTo?: string | undefined;
  /** Other factors the user could switch to. */
  available: readonly { id: string; method: string; label: string }[];
}

export interface SessionContextValue {
  status: SessionStatus;
  user: User | null;
  challenge: MfaChallenge | null;
  /** Signs in, or returns the challenge when a second factor is required. */
  signIn: (email: string, password: string) => Promise<SessionStatus>;
  /** Completes a challenge with a code. */
  verifyMfa: (code: string) => Promise<SessionStatus>;
  /** Switches to another enrolled factor and starts a fresh challenge. */
  switchMfa: (enrollmentId: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Re-reads `/me`, after a profile edit. */
  refreshUser: () => Promise<void>;
  /** Abandons a pending challenge and returns to the sign-in form. */
  cancelMfa: () => void;
  /**
   * Begins a social sign-in, returning the URL to send the browser to.
   *
   * Returns the URL rather than navigating, so the caller decides — a test
   * asserts on the URL, and a screen assigns `location.href`. A hook that
   * navigated could not be tested without a browser.
   */
  startSocial: (provider: string, redirectTo?: string) => Promise<string>;
  /**
   * Finishes one, from the `code` and `state` the provider sent back.
   *
   * No provider argument: the `state` names it, and the server reads it there.
   * The browser returns to one redirect URI for all three providers — each
   * matches it exactly against what was registered — so the client has
   * nothing to tell the server and nothing to guess.
   *
   * The same two outcomes as `signIn`: a session, or a second factor still to
   * prove. A social sign-in is not exempt from MFA — the factor belongs to the
   * account, not to the credential that opened the door.
   */
  completeSocial: (code: string, state: string) => Promise<SessionStatus>;
}

export const SessionContext = createContext<SessionContextValue | null>(null);

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) {
    throw new Error("useSession must be used inside <SessionProvider>.");
  }
  return value;
}
